// What a simulated conversation is made of, plus the small pure helpers the
// test console and its panels share.

import type { MessageButton } from "./shared";

export const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * How long to hold a bubble back before revealing it, from how much there is to read.
 *
 * WHY THIS IS NOT A CONSTANT. The console used a flat 350 ms between bubbles, which is faster than
 * anybody can read the bubble before it and reads as a dump rather than as somebody talking. The
 * client, testing on 2026-08-18: "We are showing messages all at once too fast... rather than
 * showing all three at once we will show one and then second and then third. That's how human
 * messages come, and it also gives more confidence to the person who is testing."
 *
 * A person composing on WhatsApp takes longer over a longer message, so the delay is a floor plus a
 * per-word cost, capped so a long list does not strand the reader waiting. These are presentation
 * numbers only: nothing here changes what the product sends, when it sends it, or what any log or
 * eval records. The real send is already complete before the first bubble is revealed.
 */
export const REVEAL_MIN_MS = 900;
export const REVEAL_PER_WORD_MS = 28;
export const REVEAL_MAX_MS = 2800;

export const revealDelay = (text: string): number => {
  const words = (text || "").trim().split(/\s+/).filter(Boolean).length;
  return Math.min(REVEAL_MAX_MS, REVEAL_MIN_MS + words * REVEAL_PER_WORD_MS);
};

export interface SimulationMessage {
  id: string;
  role: "user" | "bot" | "assistant";
  direction: string;
  type: string;
  content: string;
  status: string;
  whatsapp_message_id?: string;
  media_url?: string;
  media_mime_type?: string;
  media_filename?: string;
  created_at?: string;
  metadata?: { turn_metadata?: { source?: string } };
  interactive_data?: { type?: string; body?: string; buttons?: MessageButton[] } | null;
}

export interface Simulation {
  id: string;
  contact_id: string;
  whatsapp_account: string;
  phone: string;
  display_phone: string;
  profile_name: string;
  language: string;
  status: string;
}

export interface TraceItem {
  stage: string;
  status: string;
  summary?: string;
}

export interface GuardrailItem {
  name: string;
  decision: string;
  summary?: string;
}

/** Per-conversation analysis the backend lifts out of session_data. */
export interface AnalysisBlock {
  emotion?: string;
  emotion_language?: string;
  persona_archetype?: string;
  persona_confidence?: number;
  response_style?: string;
  journey?: string;
  classification?: string;
  loss_assessment?: string;
}

export interface SimulationDebug {
  quality?: Record<string, unknown>;
  analysis?: AnalysisBlock;
  session?: Record<string, unknown>;
  trace?: TraceItem[];
  sources?: unknown[];
  guardrails?: GuardrailItem[];
  eval_notes?: Record<string, unknown>;
}

export interface SimulationPayload {
  simulation?: Simulation;
  messages?: SimulationMessage[];
  session?: Record<string, unknown>;
  debug?: SimulationDebug;
}

export interface SpeakResponse {
  audio_base64?: string;
  audio_mime_type?: string;
}

export interface AccountLite {
  id?: string;
  name: string;
}
export interface AccountsResponse {
  accounts?: AccountLite[];
}

export interface ThinkStep {
  stage: string;
  status: "running" | "done";
  latency_ms?: number;
  note?: string;
}

export interface Thinking {
  active: boolean;
  steps: ThinkStep[];
  totalMs: number;
  sources: number;
}

/** Pipeline stage names in the words an operator uses, not the code's. */
export const STAGE_NAMES: Record<string, string> = {
  load_context: "Load context",
  build_evidence: "Extract evidence",
  evidence_build: "Extract evidence",
  url_analysis: "Check links and domains",
  graph_retrieve: "Read the knowledge graph",
  knowledge_retrieve: "Fetch knowledge",
  classify: "Classify (scam or not)",
  decide_action: "Decide the action",
  command_decide: "Decide the action",
  guardrail_validate: "Safety check",
  generate_response: "Write the reply",
  emit_result: "Finalise",
};

export const stageLabel = (s: string) => STAGE_NAMES[s] ?? String(s || "").replace(/_/g, " ");

export function fmtMs(ms: number | undefined): string {
  const n = Number(ms ?? 0);
  if (n >= 1000) return `${(n / 1000).toFixed(2)} s`;
  if (n >= 1) return `${n.toFixed(0)} ms`;
  return `${n.toFixed(1)} ms`;
}

export const MAX_MEDIA_BYTES = 16 * 1024 * 1024;

export const isBot = (m: SimulationMessage) => m.role === "bot" || m.role === "assistant";

/** Prefer a container the transcription backend handles well. */
export function pickRecordingMime(): string {
  const candidates = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus", "audio/webm"];
  if (typeof MediaRecorder !== "undefined" && typeof MediaRecorder.isTypeSupported === "function") {
    for (const mime of candidates) if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return "";
}

export function base64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * A reply "answered a voice turn" when the nearest preceding user message was
 * a voice note. Outgoing messages do not carry the turn's source tag, so the
 * pairing has to be inferred from the transcript order.
 */
export function isVoiceReply(m: SimulationMessage, all: SimulationMessage[]): boolean {
  if (!isBot(m)) return false;
  if (m.metadata?.turn_metadata?.source === "voice_message") return true;
  const idx = all.findIndex((x) => x.id === m.id);
  for (let i = idx - 1; i >= 0; i--) {
    if (all[i].role === "user") {
      return all[i].type === "audio" || all[i].metadata?.turn_metadata?.source === "voice_message";
    }
  }
  return false;
}
